import { isEmpty } from 'lodash-es';
import { EventPartScore, PublishedEventListItem, SportsType } from '../@types';
import { MessageType } from '../hooks/useWebsocket';
import { updateEvent, updateEventPartScores } from '../utils';
import { emitScoreChange } from '../utils/scoreChangeEmitter';

const SCORE_CHANGE_INDICATOR_SUPPORTED_SPORTS: SportsType[] = [
  'soccer',
  'water_polo',
  'ice_hockey',
  'futsal',
  'floorball',
];

export type MessageBetType = {
  odds?: string;
  id: string;
  isActive?: boolean;
  matchTime?: string;
  valueHome?: number;
  valueAway?: number;
  name?: string;
  shortName?: string;
};

type PublishedEventWithScores = PublishedEventListItem & {
  eventPartScores?: EventPartScore[];
  mainEventPart?: EventPartScore | null;
};

const updateMainEventPart = (
  payload: MessageBetType,
  eventPart?: EventPartScore | null,
  event?: PublishedEventListItem
) => {
  if (eventPart && payload.id === eventPart.id) {
    const newValueHome = payload.valueHome ?? eventPart.valueHome;
    const newValueAway = payload.valueAway ?? eventPart.valueAway;

    if (event && SCORE_CHANGE_INDICATOR_SUPPORTED_SPORTS.includes(event.tournament.sport.icon)) {
      const homeScoreIncreased =
        newValueHome != null && eventPart.valueHome != null && newValueHome > eventPart.valueHome;
      const awayScoreIncreased =
        newValueAway != null && eventPart.valueAway != null && newValueAway > eventPart.valueAway;

      if (homeScoreIncreased) {
        emitScoreChange(event.id, 'home');
      }
      if (awayScoreIncreased) {
        emitScoreChange(event.id, 'away');
      }
    }

    if (newValueHome === eventPart.valueHome && newValueAway === eventPart.valueAway) {
      return eventPart;
    }

    return {
      ...eventPart,
      valueHome: newValueHome,
      valueAway: newValueAway,
    };
  }

  return eventPart ?? null;
};

const eventListItemUpdater = (
  prev: PublishedEventWithScores & {
    eventPartScores?: EventPartScore[];
  },
  next: MessageType<MessageBetType>,
  callback?: () => void
): PublishedEventWithScores => {
  if (!prev || !next) {
    return prev;
  }

  const { event, payload } = next;

  if (isEmpty(event) || isEmpty(payload)) {
    return prev;
  }

  let updated = prev;

  if (event === 'EVENT') {
    const participants = prev.participants
      ? {
          ...prev.participants,
          home: { ...prev.participants.home },
          away: { ...prev.participants.away },
        }
      : prev.participants;

    const nextEvent = updateEvent({ ...prev, participants }, payload) as PublishedEventWithScores;
    const hasParticipantChanges =
      nextEvent.participants?.home.isHighlighted !== prev.participants?.home.isHighlighted ||
      nextEvent.participants?.away.isHighlighted !== prev.participants?.away.isHighlighted;
    const hasEventChanges =
      nextEvent.isActive !== prev.isActive ||
      nextEvent.matchTime !== prev.matchTime ||
      nextEvent.currentEventPart !== prev.currentEventPart ||
      hasParticipantChanges;

    if (hasEventChanges) {
      updated = nextEvent;
    }
  }

  if (event === 'MARKET') {
    const marketIndex = prev.topMarkets?.findIndex((market) => market.id === payload.id) ?? -1;

    if ('isActive' in payload && prev.topMarkets && marketIndex !== -1) {
      const market = prev.topMarkets[marketIndex];
      const isActive = payload.isActive ?? false;

      if (market.isActive !== isActive) {
        const topMarkets = [...prev.topMarkets];
        topMarkets[marketIndex] = { ...market, isActive };
        updated = { ...prev, topMarkets };
      }
    }
  }
  if (event === 'EVENT_PART_SCORE') {
    const mainEventPartScore = updateMainEventPart(payload, prev.mainEventPartScore, prev);
    const mainEventPart = updateMainEventPart(payload, prev.mainEventPart, prev);
    const eventPartScores = prev.eventPartScores
      ? updateEventPartScores(prev.eventPartScores, payload, callback)
      : prev.eventPartScores;

    if (
      mainEventPartScore !== prev.mainEventPartScore ||
      mainEventPart !== prev.mainEventPart ||
      eventPartScores !== prev.eventPartScores
    ) {
      updated = { ...prev, mainEventPartScore, mainEventPart, eventPartScores };
    }
  }

  return updated;
};

export default eventListItemUpdater;
